import { useEffect } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';

// view state helpers
import viewStateRepo from '../../../../core/repo/viewStateRepo';
import { tblSpecLoadStorage, tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import { ListTableInitHookReturn } from '../../../../core/table/table.types';

// IRA const and view models
import { IRA_LIST_STORAGE_KEY, IRA_LIST_VIEW_KEY } from '../../ira_const';
import {
    tableDataVM,
    tableFiltersVM,
    tableStateVM,
    tableStatsVM,
    tableViewVM,
} from '../../../../core/table/table_view_models';
import { iraListColumnSpec, iraListDefaultView } from './IraListTableSpec';

// IRA atoms to init and destroy
import {
    tableStateAtom,
    tableViewAtom,
    iraDataAtom,
    tableStatsAtom,
    tableFiltersAtom,
    setAtomIraListTableViewState,
} from '../ira_list_atoms';

// IRA data
import { IraDataItem } from '../../ira_types';
import { useIraListStore } from '../pagehooks/useIraListStore';

export const useIraListTableInit = (): ListTableInitHookReturn => {
    const pageStore = useIraListStore();
    const [{ init, waiting }, setState] = useImmerAtom(tableStateAtom);
    const setView = useSetImmerAtom(tableViewAtom);
    const setList = useSetImmerAtom(iraDataAtom);
    const setStats = useSetImmerAtom(tableStatsAtom);
    const setFilter = useSetImmerAtom(tableFiltersAtom);

    // INIT
    useEffect(() => {
        setState(draft => {
            if (draft.init && !draft.waiting) {
                console.log('TABLE INIT: GET LIST');
                // load custom views from storage
                const colSpec = iraListColumnSpec();
                const defaultView = iraListDefaultView();
                const viewStorage = tblSpecLoadStorage(IRA_LIST_VIEW_KEY, colSpec, defaultView);
                draft.views = viewStorage.views;
                draft.currentViewId = viewStorage.current;

                // set current view
                setView(() => tblSpecLoadView(viewStorage.current, viewStorage.views, colSpec) || tableViewVM());

                // viewstate filters and paging
                const viewstate = viewStateRepo.getTable(IRA_LIST_STORAGE_KEY, Array.from(colSpec.keys()));
                if (!!viewstate) {
                    setView(draft => {
                        draft.sort = viewstate.sort;
                    });
                    setFilter(() => viewstate.flt);
                    setStats(draft => {
                        draft.paging = viewstate.paging;
                    });
                }

                pageStore.getList();
                draft.waiting = true;

                return draft;
            } else if (draft.init && draft.waiting && !pageStore.loading) {
                console.log('TABLE INIT: LOAD COMPLETE');
                // wating on page store to load list
                draft.init = false;
                draft.waiting = false;
                return draft;
            } else {
                return draft;
            }
        });

        return (): void => {
            console.log('TABLE INIT: DESTROY on CHANGE == BAD');
        };
    }, [init, waiting, pageStore.loading, setState, setView, setFilter, setStats, setList, pageStore]);

    // Cleanup only on unmount, not on every init, waiting or loading change (infinite loop)
    useEffect(() => {
        return (): void => {
            console.log('TABLE INIT: DESTROY on UNMOUNT == GOOD');
            setAtomIraListTableViewState();
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => tableFiltersVM());
            setStats(() => tableStatsVM());
            setList(() => tableDataVM<IraDataItem>());
        };
    }, [setState, setView, setFilter, setStats, setList]);

    return { init };
};
